import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Status } from 'model/alerts.model';
import { BusinessesService, GetBusinessesQueryParamsType } from 'service/businesses.service';

export type BusinessType = {
  companyId: string;
  name: string;
  logo: string;
  businessType: string | null;
  categories: string[];
  activeDealCount: number;
  activeDeals: any[];
  totalSpend: number;
  clientFitScore: number;
  keyContact: {
    name: string;
    title: string;
    phone: string | null;
    email: string;
  } | null;
  tags: any[];
  lists: string[];
  website: string;
  country: string;
  employeeCount: number;
  revenue: number;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type CategoryType = { name: string; count: number };

export type PaginationType = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

type BusinessInitialStateType = {
  businesses: BusinessType[];
  categories: CategoryType[];
  myBusinessCount: number;
  allBusinessCount: number;
  pagination: PaginationType;
  loading: Status;
  tabValue: number;
  adminSearchModal: boolean;
  searchTerm: '';
  sortBy: `name` | `activeDeals` | `totalSpend` | `clientFit`;
  sortOrder: 'asc' | 'desc';
};

const initialState: BusinessInitialStateType = {
  businesses: [],
  categories: [],
  myBusinessCount: 0,
  allBusinessCount: 0,
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  loading: 'idle',
  tabValue: 0,
  adminSearchModal: false,
  searchTerm: '',
  sortBy: 'name',
  sortOrder: 'asc',
};

export const fetchBusinesses = createAsyncThunk(
  'businesses/fetchBusinesses',
  async (params?: GetBusinessesQueryParamsType) => {
    const data = await BusinessesService.getBusinesses(params);
    return data.data;
  },
);

export const fetchBusinessesByID = createAsyncThunk(
  'businesses/fetchDealById',
  async (id: string) => {
    const data = await BusinessesService.getBusinessById(id);
    return data.data;
  },
);

const businessesSlice = createSlice({
  name: 'businessesSlice',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setTabValue: (state, action) => {
      state.tabValue = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    },
    setAdminSearchModal: (state, action) => {
      state.adminSearchModal = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchBusinesses.fulfilled, (state, action) => {
      const { businesses, categories, myBusinessCount, allBusinessCount, pagination } =
        action.payload;

      state.businesses = businesses;

      state.categories = [...categories];
      state.myBusinessCount = myBusinessCount;
      state.allBusinessCount = allBusinessCount;
      state.pagination = {
        ...pagination,
        page: pagination.page,
      };
      state.loading = 'complete';
    });
    builder.addCase(fetchBusinesses.pending, (state) => {
      state.loading = 'in-progress';
    });
  },
});

export const {
  setPage,
  setTabValue,
  setPageSize,
  setAdminSearchModal,
  setSearchTerm,
  setSortBy,
  setSortOrder,
} = businessesSlice.actions;
export default businessesSlice.reducer;
